import {FC, useState, useEffect, ReactElement} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {first} from 'lodash'
import {useQuery} from 'hooks/graphql'
import {EMPLOYEE_DOCUMENTS, QueryResult, QueryVariables, Search, Order, SortColumn, Document} from './query'
import {TitleContainer} from 'components/layout/TitleContainer'
import {MainTitle} from 'components/ui/MainTitle'
import {Content} from 'components/layout/Content'
import {LoadingPage} from 'components/ui/LoadingPage'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import Button from 'jbc-front/components/Button'
import {Upload} from 'jbc-front/components/icons'
import {DocumentList} from './parts/DocumentList'
import {FileUploadModal} from './parts/FileUploadModal'
import {EmployeeDocumentContractRequestAsyncStatus} from './parts/EmployeeDocumentContractRequestAsyncStatus'
import {LinkTab} from './parts/LinkTab'

import styles from './Presenter.scss'

// @ts-ignore
import SelectList from 'components/SelectList'

export type Item = {
  item: Document
  td: ReactElement
}

type SelectListProps = {
  list: Item[]
  th: ReactElement
  selected: {[key: number]: boolean}
}

const disabledMessage = (document: Document) => {
  if (document.type == 'uploaded_document') {
    return 'ファイルアップロードにより登録された書類は、電子契約機能の対象外です。'
  } else if (document.documentEmployee.contractInfo) {
    return '電子契約送信済です。'
  } else {
    return null
  }
}
type PollingControllerProps = {
  status: string
  startPolling: (pollInterval: number) => void
  stopPolling: () => void
}
const PollingController: FC<PollingControllerProps> = ({status, startPolling, stopPolling}) => {
  useEffect(() => {
    if (status !== 'success' && status !== 'failed') {
      startPolling(3000)
    }

    return () => {
      stopPolling()
    }
  }, [status, startPolling, stopPolling])
  return null
}

export const Presenter: FC = () => {
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
  const history = useHistory()
  const {id} = useParams<{id: string}>()
  const [sortOrder, setSortOrder] = useState<Order>('desc')
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at')
  const [search, setSearch] = useState<Search>({
    sortOrder,
    sortColumn,
  })
  const {data, loading, startPolling, stopPolling} = useQuery<QueryResult, QueryVariables>(EMPLOYEE_DOCUMENTS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
    variables: {id: Number(id), search},
  })

  const client = data?.client
  const employeeDocuments = client?.employeeDocuments
  const employee = employeeDocuments?.employee
  const documents = employeeDocuments?.documents
  const employeeContractRequests = employee?.employeeDocumentContractRequests || []
  const runningRequest = employeeContractRequests.find((request) => ['waiting', 'in_progress'].includes(request.status))
  const failedRequest = employeeContractRequests.find((request) => request.status == 'failed')
  const isSelectable = !runningRequest && !failedRequest
  const latestContractRequest = first(employeeContractRequests.filter((request) => request.status == 'success'))

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      const nextOrder = inverseOrder()
      setSearch((prev) => ({
        ...prev,
        sortOrder: nextOrder,
      }))
      setSortOrder(nextOrder)
    } else {
      setSearch({
        sortOrder: 'desc',
        sortColumn: column,
      })
      setSortOrder('desc')
      setSortColumn(column)
    }
  }

  const inverseOrder = () => {
    switch (sortOrder) {
      case 'desc':
        return 'asc'
      case 'asc':
        return 'desc'
    }
  }

  const isAllowedSendContract = client?.planType === 'paid_plan' && client.isVerified

  return (
    <>
      {employee && (
        <TitleContainer className={styles.withLinkTabTitle}>
          <MainTitle>{`${employee.displayLastName} ${employee.displayFirstName}`}さんの書類</MainTitle>
          <LinkTab
            isShowContractTab={isAllowedSendContract}
            isSentContract={!!latestContractRequest}
            employeeId={String(employee.id)}
            contractRequestId={latestContractRequest?.id}
          />
        </TitleContainer>
      )}
      {employeeContractRequests.map((request) => (
        <EmployeeDocumentContractRequestAsyncStatus
          key={request.id}
          employeeId={Number(id)}
          employeeDocumentContractRequest={request}
        />
      ))}

      <Content size="xxl">
        {loading ? (
          <LoadingPage />
        ) : (
          <>
            {runningRequest && (
              <PollingController status={runningRequest.status} startPolling={startPolling} stopPolling={stopPolling} />
            )}
            {employee && (
              <div className={styles.fileUploadContainer}>
                <Button primary onClick={() => setIsFileUploadModalOpen(true)}>
                  <Upload />
                  <Spacer direction="x" size={7} />
                  ファイルアップロード
                </Button>
                <FileUploadModal
                  isOpen={isFileUploadModalOpen}
                  onClose={() => setIsFileUploadModalOpen(false)}
                  search={search}
                  employeeId={employee.id}
                />
              </div>
            )}

            <Spacer direction="y" size={20} />

            {documents && employee && (
              <SelectList list={documents} disabledMessage={disabledMessage}>
                {({list, th, selected}: SelectListProps) => {
                  const selectedDocumentIds = Object.entries(selected)
                    .filter(([_, value]) => value === true)
                    .map(([key, _]) => key)
                  const query = new URLSearchParams()
                  selectedDocumentIds.forEach((documentId) => {
                    query.append('documentIds[]', String(documentId))
                  })

                  return (
                    <>
                      <DocumentList
                        isSelectable={isSelectable}
                        employeeId={employee.id}
                        documents={list}
                        th={th}
                        onSort={handleSort}
                        order={sortOrder}
                        sortColumn={sortColumn}
                      />
                      <Spacer direction="y" size={30} />

                      <div className="u-ta-c">
                        {isAllowedSendContract && (
                          <Button
                            primary
                            onClick={() =>
                              history.push(`/documents/employees/${employee?.id}/contracts/create?${query}`)
                            }
                            disabled={selectedDocumentIds.length === 0 || !isSelectable}
                          >
                            電子契約送信
                          </Button>
                        )}
                      </div>
                    </>
                  )
                }}
              </SelectList>
            )}
          </>
        )}
      </Content>
    </>
  )
}
